import {
	logScreenshareErrorApi,
	uploadScreenshareSnapshotApi,
} from '../../apiCalls'
import { gzipText } from './gzip'

// Must stay aligned with the api-gateway / api-registry limits for the screenshare route.
export const MAX_SNAPSHOT_HTML_BYTES = 5 * 1024 * 1024
export const MAX_SNAPSHOT_GZIP_BYTES = 1024 * 1024
export const FAILURE_REPORT_INTERVAL_MS = 60 * 1000

// 'too-large' will not change until the page does, so callers should not retry the same HTML.
export type SnapshotUploadResult = 'uploaded' | 'too-large' | 'failed'

let lastFailureReportAt = 0
let failuresSinceLastReport = 0

// Uploads retry on every DOM change, so failures reach the backend log at most once per interval.
const reportFailure = (reason: string) => {
	console.warn(`Screenshare snapshot not uploaded: ${reason}`)
	failuresSinceLastReport += 1

	const now = Date.now()
	if (now - lastFailureReportAt < FAILURE_REPORT_INTERVAL_MS) return

	logScreenshareErrorApi(
		`Screenshare snapshot not uploaded: ${reason} (${failuresSinceLastReport} failures since last report)`,
	).catch(() => undefined)
	lastFailureReportAt = now
	failuresSinceLastReport = 0
}

/**
 * Uploads the gzip-compressed snapshot; the backend stores it and notifies the exam group.
 */
export const uploadSnapshot = async (
	examId: string,
	html: string,
): Promise<SnapshotUploadResult> => {
	try {
		// UTF-16 length is a cheap lower bound of the byte size; the gzip check below is the real limit.
		if (html.length > MAX_SNAPSHOT_HTML_BYTES) {
			reportFailure(`${html.length} characters of HTML exceed the limit`)
			return 'too-large'
		}

		const gzipped = await gzipText(html)
		if (gzipped.size > MAX_SNAPSHOT_GZIP_BYTES) {
			reportFailure(`${gzipped.size} compressed bytes exceed the limit`)
			return 'too-large'
		}

		const response = await uploadScreenshareSnapshotApi(examId, gzipped)
		if (!response.ok) {
			reportFailure(`upload failed with status ${response.status}`)
			return response.status === 413 ? 'too-large' : 'failed'
		}
		return 'uploaded'
	} catch (error) {
		reportFailure(`upload failed: ${(error as Error)?.message ?? error}`)
		return 'failed'
	}
}
